import { create } from "zustand"

export type ColorTheme = "default" | "blue" | "green" | "rose" | "orange" | "teal"
export type SidebarTheme =
  | "purple-galaxy"
  | "lavender"
  | "ocean-blue"
  | "forest-green"
  | "sunset"
  | "midnight"
  | "slate"

function getCookie(name: string): string | undefined {
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`))
  return match?.split("=")[1]
}

function setCookie(name: string, value: string) {
  document.cookie = `${name}=${value}; path=/; max-age=${60 * 60 * 24 * 365}`
}

function applyColorTheme(theme: ColorTheme) {
  if (theme === "default") {
    document.documentElement.removeAttribute("data-color-theme")
  } else {
    document.documentElement.setAttribute("data-color-theme", theme)
  }
}

function applySidebarGradient(theme: SidebarTheme) {
  const def = sidebarThemeDefinitions[theme]
  // text / brand colors per sidebar theme live in index.css
  document.documentElement.setAttribute("data-sidebar-theme", theme)
  document.documentElement.style.setProperty("--sidebar-gradient-light", def.light)
  document.documentElement.style.setProperty("--sidebar-gradient-dark", def.dark)
}

export const sidebarThemeDefinitions: Record<
  SidebarTheme,
  { label: string; light: string; dark: string; preview: string }
> = {
  "purple-galaxy": {
    label: "Atlant",
    light: "linear-gradient(173.77deg, #263B8A 9.13%, #642C8D 42.79%, #981D8F 100%)",
    dark: "linear-gradient(173.77deg, #121639 9.13%, #111026 42.79%, #140D20 100%)",
    preview: "#642C8D",
  },
  lavender: {
    label: "Lavender",
    light: "linear-gradient(173.77deg, #C8CEFF 9.13%, #EBEAFF 42.79%, #F0E8FF 100%)",
    dark: "linear-gradient(173.77deg, #1E1F45 9.13%, #221D44 42.79%, #2A1E4A 100%)",
    preview: "#C8CEFF",
  },
  "ocean-blue": {
    label: "Ocean Blue",
    light: "linear-gradient(173.77deg, #1a3a5c 9.13%, #1e5080 42.79%, #2563eb 100%)",
    dark: "linear-gradient(173.77deg, #0c1929 9.13%, #0f2440 42.79%, #132e52 100%)",
    preview: "#1e5080",
  },
  "forest-green": {
    label: "Forest Green",
    light: "linear-gradient(173.77deg, #1a4a3a 9.13%, #166534 42.79%, #15803d 100%)",
    dark: "linear-gradient(173.77deg, #0a1f17 9.13%, #0c291e 42.79%, #0f3526 100%)",
    preview: "#166534",
  },
  sunset: {
    label: "Sunset",
    light: "linear-gradient(173.77deg, #9a3412 9.13%, #c2410c 42.79%, #ea580c 100%)",
    dark: "linear-gradient(173.77deg, #2a1008 9.13%, #3b1506 42.79%, #4a1c0a 100%)",
    preview: "#c2410c",
  },
  midnight: {
    label: "Midnight",
    light: "linear-gradient(173.77deg, #1e1b4b 9.13%, #1e1e38 42.79%, #312e81 100%)",
    dark: "linear-gradient(173.77deg, #0a0a1a 9.13%, #0f0f1f 42.79%, #151530 100%)",
    preview: "#1e1e38",
  },
  slate: {
    label: "Slate",
    light: "linear-gradient(173.77deg, #334155 9.13%, #475569 42.79%, #64748b 100%)",
    dark: "linear-gradient(173.77deg, #0f172a 9.13%, #1e293b 42.79%, #1e293b 100%)",
    preview: "#475569",
  },
}

export const colorThemeDefinitions: Record<
  ColorTheme,
  { label: string; preview: string }
> = {
  default: { label: "Atlant", preview: "#6347ea" },
  blue: { label: "Blue", preview: "#2563eb" },
  green: { label: "Green", preview: "#16a34a" },
  rose: { label: "Rose", preview: "#e11d48" },
  orange: { label: "Orange", preview: "#ea580c" },
  teal: { label: "Teal", preview: "#0d9488" },
}

const initialColorTheme = (getCookie("color_theme") as ColorTheme) || "default"
const initialSidebarTheme =
  (getCookie("sidebar_theme") as SidebarTheme) || "purple-galaxy"

applyColorTheme(initialColorTheme)
applySidebarGradient(initialSidebarTheme)

interface AppearanceState {
  colorTheme: ColorTheme
  sidebarTheme: SidebarTheme
  setColorTheme: (theme: ColorTheme) => void
  setSidebarTheme: (theme: SidebarTheme) => void
}

export const useAppearanceStore = create<AppearanceState>((set) => ({
  colorTheme: initialColorTheme,
  sidebarTheme: initialSidebarTheme,
  setColorTheme: (theme) => {
    setCookie("color_theme", theme)
    applyColorTheme(theme)
    set({ colorTheme: theme })
  },
  setSidebarTheme: (theme) => {
    setCookie("sidebar_theme", theme)
    applySidebarGradient(theme)
    set({ sidebarTheme: theme })
  },
}))
